/**
 * Drill Stage Route
 *
 * Triggers loading of the drill stage when this route becomes active.
 * Supports GLB variants via ?glb query parameter.
 * The actual canvas is in the parent layout (ux.tsx).
 *
 * When a non-Babylon viewer is selected, the BabylonJS stage load is skipped
 * and a lazy-loaded Three.js or model-viewer component renders the GLB instead.
 */

import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useMemo } from "react";
import { z } from "zod";
import { useViewerRefs } from "@/babylon/context/ViewerRefsContext";
import {
  DEFAULT_DRILL_GLB_KEY,
  DRILL_GLB_CONFIGS,
} from "@/babylon/stages/drill.stage";
import { resolveDrillGlbUrl } from "@/babylon/utils/glbPaths";
import { useViewer } from "@/context/ViewerContext";
import { useLoadStageOnMount } from "../ux";

const ThreeGlbViewer = lazy(
  () => import("@/babylon/components/viewers/ThreeGlbViewer"),
);
const ModelViewerGlb = lazy(
  () => import("@/babylon/components/viewers/ModelViewerGlb"),
);

/** Valid GLB keys derived from config */
const drillGlbKeys = Object.keys(DRILL_GLB_CONFIGS) as [string, ...string[]];

/** Zod schema for drill route search params */
const drillSearchSchema = z.object({
  glb: z.enum(drillGlbKeys).optional().default(DEFAULT_DRILL_GLB_KEY),
});

export const Route = createFileRoute("/_immersive/demo/3d/ux/drill")({
  component: DrillStageRoute,
  validateSearch: (search) => drillSearchSchema.parse(search),
});

function DrillStageRoute() {
  const { glb } = Route.useSearch();
  const { viewerType } = useViewer();
  const { setThreeRefs, setModelViewerElement } = useViewerRefs();
  const glbUrl = resolveDrillGlbUrl(glb);

  // Memoize config overrides to avoid unnecessary re-renders
  const configOverrides = useMemo(() => ({ glbKey: glb }), [glb]);

  // Always call hook unconditionally (React rules of hooks), gate with enabled
  useLoadStageOnMount("drill", configOverrides, viewerType === "babylon");

  if (viewerType === "babylon") return null;

  return (
    <div className="w-full h-full pointer-events-auto">
      <Suspense
        fallback={
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            Loading viewer...
          </div>
        }
      >
        {viewerType === "three.js" && (
          <ThreeGlbViewer glbUrl={glbUrl} onRefsReady={setThreeRefs} />
        )}
        {viewerType === "model-viewer" && (
          <ModelViewerGlb
            glbUrl={glbUrl}
            onElementReady={setModelViewerElement}
          />
        )}
      </Suspense>
    </div>
  );
}
